import React, { useState, useMemo } from 'react';
import {
    Button,
    IconButton,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper,
    InputAdornment,
    Box,
    Typography,
    Autocomplete,
    Popper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import DropzoneArea from '../DropzoneArea';
import { styled } from '@mui/material/styles';
import Constants from '../../../../assests/Constants/constants';

const BorderBoxButton = styled(Button)({
    boxSizing: 'border-box !important',
});

const StyledPopper = styled(Popper)(({ theme }) => ({
    zIndex: '30000 !important',
    '& .MuiPaper-root': {
        borderRadius: '8px',
        background: '#FFF',
        boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.15)',
        minWidth: '200px',
    },
    '& .MuiAutocomplete-listbox': {
        padding: theme.spacing(1),
        maxHeight: '280px',
    },
    '& .MuiAutocomplete-option': {
        borderRadius: '4px',
        padding: '8px 12px',
        fontSize: '0.875rem',
        margin: '2px 0',
        '&[aria-selected="true"]': {
            background: 'rgba(48, 79, 255, 0.12) !important',
            fontWeight: 600,
        },
        '&.Mui-focused': {
            background: 'rgba(249, 170, 42, 0.15) !important',
        },
        '&:hover': {
            background: 'rgba(249, 170, 42, 0.25) !important',
        },
    },
}));

const WebenefitJournalImportModal = ({
    open,
    onClose,
    previewWebenefitJournal,
    commitWebenefitJournal,
    createMissingSortCodes,
    direction,
    t,
    formattedSortCodeVatType = [],
    formattedSortCodesGroupNamesOptions = [],
}) => {
    const [file, setFile] = useState(null);
    const [previewDone, setPreviewDone] = useState(false);
    const [processing, setProcessing] = useState(false);

    const [missingSortCodes, setMissingSortCodes] = useState([]);
    const [editableMissing, setEditableMissing] = useState([]);
    const [journalEntries, setJournalEntries] = useState([]);
    const [parsedRows, setParsedRows] = useState(0);
    const [duplicateCount, setDuplicateCount] = useState(0);
    const [newRows, setNewRows] = useState(0);

    // Table search & pagination
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(100);

    const filteredEntries = useMemo(() => {
        if (!searchTerm.trim()) return journalEntries;
        const term = searchTerm.trim().toLowerCase();
        return journalEntries.filter(row =>
            (row.sortCodeName || '').toLowerCase().includes(term) ||
            (row.supplierName || '').toLowerCase().includes(term) ||
            (row.accountCaseId || '').toLowerCase().includes(term) ||
            (row.reference || '').toLowerCase().includes(term) ||
            (row.confirmationNumber || '').toLowerCase().includes(term) ||
            (row.description || '').toLowerCase().includes(term) ||
            String(row.totalAmount ?? '').includes(term) ||
            String(row.baseAmount ?? '').includes(term) ||
            String(row.vatAmount ?? '').includes(term)
        );
    }, [journalEntries, searchTerm]);

    const paginatedEntries = useMemo(() => {
        const start = page * rowsPerPage;
        return filteredEntries.slice(start, start + rowsPerPage);
    }, [filteredEntries, page, rowsPerPage]);

    // Bulk edit state
    const [selectedRows, setSelectedRows] = useState([]);
    const [bulkVatType, setBulkVatType] = useState(null);
    const [bulkGroup, setBulkGroup] = useState(null);
    const [bulkPar, setBulkPar] = useState(null);
    const [bulkVat, setBulkVat] = useState(null);
    const [bulkFieldsChanged, setBulkFieldsChanged] = useState({
        vatType: false,
        group: false,
        par: false,
        vat: false
    });

    const handleFileDrop = files => {
        if (files?.length) setFile(files[0]);
        setPreviewDone(false);
        setMissingSortCodes([]);
        setEditableMissing([]);
        setJournalEntries([]);
        setParsedRows(0);
        setDuplicateCount(0);
        setNewRows(0);
        setSelectedRows([]);
        setSearchTerm('');
        setPage(0);
    };

    const handlePreview = async () => {
        if (!file) return;

        try {
            setProcessing(true);
            setPreviewDone(false);

            const res = await previewWebenefitJournal(file);

            const missing = res?.missingSortCodes ?? [];
            const rows = res?.parsedRows ?? 0;
            const dupes = res?.duplicateCount ?? 0;
            const newR = res?.newRows ?? rows;

            setMissingSortCodes(missing);
            setJournalEntries(res?.journalEntries ?? []);
            setParsedRows(rows);
            setDuplicateCount(dupes);
            setNewRows(newR);

            setEditableMissing(missing.map(sc => ({
                ...sc,
                sortCodeId: sc.sortCodeId || '',
                sortCodeName: sc.sortCodeName || '',
                sortCodeVatType: sc.sortCodeVatType ?? null,
                sortCodeCategory: sc.sortCodeCategory ?? null,
                participatesInProfitAndLoss: sc.participatesInProfitAndLoss ?? null,
                vat: sc.vat ?? null
            })));

            setPreviewDone(true);
        } catch (err) {
            console.error('Webenefit journal preview failed:', err);
            setPreviewDone(false);
        } finally {
            setProcessing(false);
        }
    };

    const handleCreateAndCommit = async () => {
        if (!file) return;

        try {
            setProcessing(true);

            if (editableMissing && editableMissing.length > 0) {
                await createMissingSortCodes(editableMissing);
            }

            const res = await commitWebenefitJournal(file);

            const errorCount = res?.errorCount ?? res?.[1] ?? 0;
            const errorMessages = res?.errorMessages ?? res?.[2] ?? [];

            if (errorCount > 0 && errorMessages.length > 0) {
                console.error('Import errors:', errorMessages);
            } else {
                handleClose();
            }
        } catch (err) {
            console.error('Webenefit journal commit error:', err);
        } finally {
            setProcessing(false);
        }
    };

    const updateMissing = (index, field, value) =>
        setEditableMissing(prev => {
            const arr = [...prev];
            arr[index] = { ...arr[index], [field]: value };
            return arr;
        });

    const applyBulk = () => {
        setEditableMissing(prev =>
            prev.map(r =>
                selectedRows.includes(r.sortCodeId)
                    ? {
                        ...r,
                        sortCodeVatType: bulkFieldsChanged.vatType ? (bulkVatType?.value ?? null) : r.sortCodeVatType,
                        sortCodeCategory: bulkFieldsChanged.group ? (bulkGroup?.value ?? null) : r.sortCodeCategory,
                        participatesInProfitAndLoss: bulkFieldsChanged.par ? (bulkPar?.value ?? null) : r.participatesInProfitAndLoss,
                        vat: bulkFieldsChanged.vat ? (bulkVat?.value ?? null) : r.vat,
                    }
                    : r
            )
        );
        setSelectedRows([]);
        setBulkVatType(null);
        setBulkGroup(null);
        setBulkPar(null);
        setBulkVat(null);
        setBulkFieldsChanged({ vatType: false, group: false, par: false, vat: false });
    };

    const missingIncomplete = useMemo(
        () => editableMissing.some(sc =>
            !sc.sortCodeName ||
            !sc.sortCodeName.trim() ||
            sc.sortCodeVatType === null ||
            sc.sortCodeVatType === undefined ||
            sc.participatesInProfitAndLoss === null ||
            sc.participatesInProfitAndLoss === undefined ||
            sc.vat === null ||
            sc.vat === undefined
        ),
        [editableMissing]
    );

    const handleClose = () => {
        setFile(null);
        setPreviewDone(false);
        setMissingSortCodes([]);
        setEditableMissing([]);
        setJournalEntries([]);
        setParsedRows(0);
        setDuplicateCount(0);
        setNewRows(0);
        setSelectedRows([]);
        setSearchTerm('');
        setPage(0);
        setRowsPerPage(100);
        onClose();
    };

    if (!open) return null;

    return (
        <div className="download-modal-overlay" onClick={handleClose} style={{ zIndex: 2000 }}>
            <div className="download-modal" onClick={e => e.stopPropagation()} style={{ zIndex: 2001 }}>
                <div className={`download-modal-content ${direction}`} style={{ position: 'relative', maxWidth: previewDone ? '1400px' : '600px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                        <h4 className="report-title">
                            {t('ImportWebenefitJournalEntries') || 'ייבוא פקודות יומן מ-Webenefit'}
                        </h4>
                        <IconButton
                            className="client-back-icon"
                            onClick={handleClose}
                            sx={{ padding: '4px' }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </div>

                    <Box display="flex" flexDirection="column" gap={2}>
                        <div>
                            <h5>{t('JournalEntriesFile') || 'קובץ פקודות יומן'} (.xlsx/.xls)</h5>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                {t('WebenefitJournalFileHelp') ||
                                    'בחר/י קובץ Excel שהופק מ-Webenefit. לאחר Preview ניתן להשלים פרטי סוגי עסקה חסרים ולהמשיך לייבוא.'}
                            </Typography>
                            <DropzoneArea
                                key={file ? file.name : 'webenefit-journal-empty'}
                                onDrop={handleFileDrop}
                                accept=".xls,.xlsx"
                                filesLimit={1}
                            />
                            {file && (
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    {t('FileSelected')}: {file.name}
                                </Typography>
                            )}
                        </div>
                    </Box>

                    <div style={{ display: 'flex', gap: 15, marginTop: 20 }}>
                        <Button
                            variant="contained"
                            onClick={handlePreview}
                            disabled={!file || processing}
                        >
                            {t('Preview')}
                        </Button>
                        <Button
                            variant="contained"
                            color="success"
                            disabled={!previewDone || processing || missingIncomplete}
                            onClick={handleCreateAndCommit}
                        >
                            {processing ? t('Processing') : t('CreateAndImport')}
                        </Button>
                    </div>

                    {previewDone && (
                        <div style={{ marginTop: 25, maxHeight: 800, overflow: 'auto' }}>
                            <div style={{ marginBottom: 15, display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                                <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>
                                    {t('ParsedRows') || 'שורות שנקראו'}: <strong>{parsedRows}</strong>
                                </p>
                                {duplicateCount > 0 && (
                                    <p style={{ fontSize: '14px', color: '#f57c00', margin: 0 }}>
                                        {t('DuplicateRows') || 'כפילויות'}: <strong>{duplicateCount}</strong>
                                    </p>
                                )}
                                <p style={{ fontSize: '14px', color: '#2e7d32', margin: 0 }}>
                                    {t('NewRowsToImport') || 'שורות חדשות לייבוא'}: <strong>{newRows}</strong>
                                </p>
                            </div>

                            {editableMissing.length === 0 ? (
                                <p style={{ color: 'green' }}>{t('NoMissingSortCodes')}</p>
                            ) : (
                                <>
                                    <h5>{t('MissingSortCodes')}</h5>
                                    {/* Bulk Edit Controls */}
                                    <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
                                        <Autocomplete
                                            options={formattedSortCodeVatType || []}
                                            getOptionLabel={(option) => option?.label ?? ''}
                                            value={bulkVatType}
                                            onChange={(e, newValue) => {
                                                setBulkVatType(newValue);
                                                setBulkFieldsChanged((prev) => ({ ...prev, vatType: true }));
                                            }}
                                            isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                            renderInput={(params) => (
                                                <TextField {...params} placeholder={t('SelectSortCodeVatType')} size="small" />
                                            )}
                                            PopperComponent={StyledPopper}
                                            sx={{ minWidth: 180 }}
                                        />
                                        <Autocomplete
                                            options={formattedSortCodesGroupNamesOptions || []}
                                            getOptionLabel={(option) => option?.label ?? ''}
                                            value={bulkGroup}
                                            onChange={(e, newValue) => {
                                                setBulkGroup(newValue);
                                                setBulkFieldsChanged((prev) => ({ ...prev, group: true }));
                                            }}
                                            isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                            renderInput={(params) => (
                                                <TextField {...params} placeholder={t('SelectSortCodeGroupName')} size="small" />
                                            )}
                                            PopperComponent={StyledPopper}
                                            sx={{ minWidth: 180 }}
                                        />
                                        <Autocomplete
                                            options={[
                                                { label: t('Yes'), value: true },
                                                { label: t('No'), value: false },
                                            ]}
                                            getOptionLabel={(option) => option?.label ?? ''}
                                            value={bulkPar}
                                            onChange={(e, newValue) => {
                                                setBulkPar(newValue);
                                                setBulkFieldsChanged((prev) => ({ ...prev, par: true }));
                                            }}
                                            isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                            renderInput={(params) => (
                                                <TextField {...params} placeholder={t('participatesInProfitAndLoss')} size="small" />
                                            )}
                                            PopperComponent={StyledPopper}
                                            sx={{ minWidth: 160 }}
                                        />
                                        <Autocomplete
                                            options={(Constants?.VATPercentage || []).map((v) => ({ label: `${v}%`, value: v }))}
                                            getOptionLabel={(option) => option?.label ?? ''}
                                            value={bulkVat}
                                            onChange={(e, newValue) => {
                                                setBulkVat(newValue);
                                                setBulkFieldsChanged((prev) => ({ ...prev, vat: true }));
                                            }}
                                            isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                            renderInput={(params) => (
                                                <TextField {...params} placeholder={t('VAT')} size="small" />
                                            )}
                                            PopperComponent={StyledPopper}
                                            sx={{ minWidth: 120 }}
                                        />
                                        <BorderBoxButton
                                            variant="outlined"
                                            disabled={!bulkFieldsChanged.vatType && !bulkFieldsChanged.group && !bulkFieldsChanged.par && !bulkFieldsChanged.vat}
                                            onClick={applyBulk}
                                        >
                                            {t('ApplyToSelected')}
                                        </BorderBoxButton>
                                    </div>

                                    <table className="sortcode-table editable">
                                        <thead>
                                            <tr>
                                                <th style={{ width: '5%' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedRows.length === editableMissing.length && editableMissing.length > 0}
                                                        onChange={e => setSelectedRows(e.target.checked ? editableMissing.map(r => r.sortCodeId) : [])}
                                                    />
                                                </th>
                                                <th>{t('SortCode Id')}</th>
                                                <th>{t('SortCode Name')}</th>
                                                <th>{t('SelectSortCodeVatType')}</th>
                                                <th>{t('SelectSortCodeGroupName')}</th>
                                                <th>{t('participatesInProfitAndLoss')}</th>
                                                <th>{t('VAT')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {editableMissing.map((sc, i) => (
                                                <tr key={sc.sortCodeId}>
                                                    <td>
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedRows.includes(sc.sortCodeId)}
                                                            onChange={e => setSelectedRows(prev => e.target.checked ? [...prev, sc.sortCodeId] : prev.filter(id => id !== sc.sortCodeId))}
                                                        />
                                                    </td>
                                                    <td>
                                                        <TextField
                                                            fullWidth
                                                            value={sc.sortCodeId}
                                                            variant="standard"
                                                            InputProps={{
                                                                readOnly: true,
                                                                disableUnderline: true,
                                                                sx: {
                                                                    height: '48px',
                                                                    padding: '0 10px',
                                                                    borderRadius: '8px',
                                                                    background: '#F5F5F6',
                                                                    border: '1px solid transparent'
                                                                }
                                                            }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <TextField
                                                            fullWidth
                                                            value={sc.sortCodeName || ''}
                                                            variant="standard"
                                                            onChange={e => updateMissing(i, 'sortCodeName', e.target.value)}
                                                            placeholder={t('EnterSortCodeName') || 'Enter sortcode name'}
                                                            InputProps={{
                                                                disableUnderline: true,
                                                                sx: {
                                                                    height: '48px',
                                                                    padding: '0 10px',
                                                                    borderRadius: '8px',
                                                                    background: (!sc.sortCodeName || !sc.sortCodeName.trim()) ? 'white' : '#F5F5F6',
                                                                    border: (!sc.sortCodeName || !sc.sortCodeName.trim()) ? '1.5px solid #f44336' : '1px solid transparent',
                                                                },
                                                            }}
                                                            inputProps={{
                                                                style: {
                                                                    textAlign: direction === 'ltr' ? 'left' : 'right',
                                                                },
                                                            }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <Autocomplete
                                                            options={formattedSortCodeVatType || []}
                                                            getOptionLabel={(option) => option?.label ?? ''}
                                                            value={
                                                                (formattedSortCodeVatType || []).find(
                                                                    (opt) => opt.value === sc.sortCodeVatType?.toString()?.trim()
                                                                ) || null
                                                            }
                                                            onChange={(e, newValue) => updateMissing(i, 'sortCodeVatType', newValue?.value ?? null)}
                                                            isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    placeholder={t('SelectSortCodeVatType')}
                                                                    size="small"
                                                                    error={sc.sortCodeVatType == null}
                                                                />
                                                            )}
                                                            PopperComponent={StyledPopper}
                                                            sx={{ minWidth: 180 }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <Autocomplete
                                                            options={formattedSortCodesGroupNamesOptions || []}
                                                            getOptionLabel={(option) => option?.label ?? ''}
                                                            value={
                                                                (formattedSortCodesGroupNamesOptions || []).find(
                                                                    (opt) => opt.value === sc.sortCodeCategory
                                                                ) || null
                                                            }
                                                            onChange={(e, newValue) => updateMissing(i, 'sortCodeCategory', newValue?.value ?? null)}
                                                            isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    placeholder={t('SelectSortCodeGroupName')}
                                                                    size="small"
                                                                />
                                                            )}
                                                            PopperComponent={StyledPopper}
                                                            sx={{ minWidth: 180 }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <Autocomplete
                                                            options={[
                                                                { label: t('Yes'), value: true },
                                                                { label: t('No'), value: false },
                                                            ]}
                                                            getOptionLabel={(option) => option?.label ?? ''}
                                                            value={
                                                                sc.participatesInProfitAndLoss === true
                                                                    ? { label: t('Yes'), value: true }
                                                                    : sc.participatesInProfitAndLoss === false
                                                                      ? { label: t('No'), value: false }
                                                                      : null
                                                            }
                                                            onChange={(e, newValue) =>
                                                                updateMissing(i, 'participatesInProfitAndLoss', newValue?.value ?? null)
                                                            }
                                                            isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    placeholder={t('participatesInProfitAndLoss')}
                                                                    size="small"
                                                                    error={sc.participatesInProfitAndLoss == null}
                                                                />
                                                            )}
                                                            PopperComponent={StyledPopper}
                                                            sx={{ minWidth: 180 }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <Autocomplete
                                                            options={(Constants?.VATPercentage || []).map((v) => ({ label: `${v}%`, value: v }))}
                                                            getOptionLabel={(option) => option?.label ?? ''}
                                                            value={
                                                                (Constants?.VATPercentage || [])
                                                                    .map((v) => ({ label: `${v}%`, value: v }))
                                                                    .find((opt) => opt.value === sc.vat) || null
                                                            }
                                                            onChange={(e, newValue) => updateMissing(i, 'vat', newValue?.value ?? null)}
                                                            isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    placeholder={t('VAT')}
                                                                    size="small"
                                                                    error={sc.vat == null}
                                                                />
                                                            )}
                                                            PopperComponent={StyledPopper}
                                                            sx={{ minWidth: 120 }}
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </>
                            )}

                            {/* Parsed Rows Table */}
                            {journalEntries.length > 0 && (
                                <div style={{ marginTop: 25 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                        <h5 style={{ margin: 0 }}>{t('ParsedRows') || 'שורות שנקראו'} ({filteredEntries.length}{searchTerm ? ` / ${journalEntries.length}` : ''})</h5>
                                        <TextField
                                            size="small"
                                            placeholder={t('Search') || 'חיפוש'}
                                            value={searchTerm}
                                            onChange={e => { setSearchTerm(e.target.value); setPage(0); }}
                                            sx={{ width: 250 }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </div>
                                    <Paper variant="outlined" sx={{ borderRadius: '8px' }}>
                                        <TableContainer sx={{ maxHeight: 450 }}>
                                            <Table size="small" stickyHeader>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                                                        <TableCell sx={{ fontWeight: 600 }}>{t('SortCode') || 'סוג'}</TableCell>
                                                        <TableCell sx={{ fontWeight: 600 }}>{t('SupplierCustomer') || 'ספק/לקוח'}</TableCell>
                                                        <TableCell sx={{ fontWeight: 600 }}>{t('AccountCaseId') || 'ח.פ.'}</TableCell>
                                                        <TableCell sx={{ fontWeight: 600 }}>{t('DocumentDate') || 'תאריך'}</TableCell>
                                                        <TableCell sx={{ fontWeight: 600 }}>{t('Reference') || 'אסמכתא'}</TableCell>
                                                        <TableCell sx={{ fontWeight: 600 }}>{t('BaseAmount') || 'סכום'}</TableCell>
                                                        <TableCell sx={{ fontWeight: 600 }}>{t('VAT') || 'מע"מ'}</TableCell>
                                                        <TableCell sx={{ fontWeight: 600 }}>{t('TotalAmount') || 'סה"כ'}</TableCell>
                                                        <TableCell sx={{ fontWeight: 600 }}>{t('ConfirmationNumber') || 'מספר הקצאה'}</TableCell>
                                                        <TableCell sx={{ fontWeight: 600 }}>{t('Description') || 'הערות'}</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {paginatedEntries.map((row, idx) => (
                                                        <TableRow key={idx} hover>
                                                            <TableCell>{page * rowsPerPage + idx + 1}</TableCell>
                                                            <TableCell>{row.sortCodeName || ''}</TableCell>
                                                            <TableCell>{row.supplierName || ''}</TableCell>
                                                            <TableCell>{row.accountCaseId || ''}</TableCell>
                                                            <TableCell>{row.documentDate ? new Date(row.documentDate).toLocaleDateString() : ''}</TableCell>
                                                            <TableCell>{row.reference || ''}</TableCell>
                                                            <TableCell>{row.baseAmount?.toLocaleString() ?? ''}</TableCell>
                                                            <TableCell>{row.vatAmount?.toLocaleString() ?? ''}</TableCell>
                                                            <TableCell>{row.totalAmount?.toLocaleString() ?? ''}</TableCell>
                                                            <TableCell>{row.confirmationNumber || ''}</TableCell>
                                                            <TableCell>{row.description || ''}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                        <TablePagination
                                            component="div"
                                            count={filteredEntries.length}
                                            page={page}
                                            onPageChange={(_, newPage) => setPage(newPage)}
                                            rowsPerPage={rowsPerPage}
                                            onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                                            rowsPerPageOptions={[100, 200, 500]}
                                            labelRowsPerPage={t('RowsPerPage') || 'שורות בעמוד'}
                                        />
                                    </Paper>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default WebenefitJournalImportModal;
